import {controller, target, targets} from '@github/catalyst'

import {loaded} from '@github-ui/document-ready'
import {updateContent} from '../updatable-content'

const DRAG_THRESHOLD_PX = 5

const DESIRED_CONNECTOR_RADIUS = 24
const DISTANCE_BETWEEN_CARDS = 56
const MAX_RECURSION_DEPTH = 256

class MouseDriver {
  private dragStartX?: number
  private dragStartY?: number
  private dragging: boolean
  private lastPoint: {x: number; y: number}
  private graph: ActionGraphElement

  constructor(graph: ActionGraphElement) {
    this.dragging = false
    this.graph = graph
    this.lastPoint = {
      x: 0,
      y: 0,
    }
    this.setupListener()
  }

  private setupListener() {
    document.body.addEventListener('pointerup', () => this.up())
    document.body.addEventListener('pointermove', e => this.move(e))
    this.graph.addEventListener('pointerdown', e => this.down(e))
    this.graph.addEventListener('pointermove', e => this.move(e))

    // Register a capture handler, we need to be able to prevent the default action for children
    this.graph.addEventListener('click', e => this.click(e), {
      capture: true,
    })

    // This cannot be passive, we need to be able to prevent the default behavior
    // eslint-disable-next-line github/require-passive-events
    this.graph.addEventListener('wheel', (event: WheelEvent) => this.handleWheel(event), {
      capture: false,
      passive: false,
    })
  }

  private updateCursor() {
    if (this.dragging) {
      this.graph.canvas.classList.add('dragging')
    } else {
      this.graph.canvas.classList.remove('dragging')
    }
  }

  private up() {
    this.dragging = false

    this.updateCursor()
  }

  private click(event: MouseEvent) {
    if (
      Math.abs(this.dragStartX! - event.pageX) > DRAG_THRESHOLD_PX ||
      Math.abs(this.dragStartY! - event.pageY) > DRAG_THRESHOLD_PX
    ) {
      // Mouse has been moved while dragging, cancel the default action (e.g., a click on another interactive
      // element in the graph)
      event.preventDefault()
      event.stopPropagation()
    }
  }

  private down(event: PointerEvent) {
    if (
      // Only track the main (i.e., left) mouse button
      event.button !== 0 ||
      // This event is fired continously when using touch, only handle it the first time
      this.dragging
    ) {
      return
    }

    this.dragging = true

    // Store the inital position of this drag operation
    this.dragStartX = event.pageX
    this.dragStartY = event.pageY

    // Store the last x/y position for the delta calculation once the pointer is moved
    this.lastPoint.x = event.pageX
    this.lastPoint.y = event.pageY

    this.updateCursor()
  }

  private move(e: PointerEvent) {
    const x = e.pageX
    const y = e.pageY

    if (this.dragging) {
      const dx = e.pageX - this.lastPoint.x
      const dy = e.pageY - this.lastPoint.y

      this.graph.dragMove(dx, dy)
    }

    this.lastPoint.x = x
    this.lastPoint.y = y
  }

  private handleWheel(e: WheelEvent) {
    const oldZoom = this.graph.getCurrentZoom()
    this.graph.setNormalizedZoom(this.graph.getCurrentZoom() - e.deltaY / 100)
    this.graph.setFitToWindowBtnEnabled(true)
    this.moveZoomOffset(e, oldZoom)
    e.stopPropagation()
    e.stopImmediatePropagation()
    e.preventDefault()
  }

  private moveZoomOffset(e: WheelEvent, oldZoom: number) {
    // This moves the graph so that the scale is centered to the mouse pointer
    const box = this.graph.visualGraph.getBoundingClientRect()
    const ratio = (this.graph.getCurrentZoom() - oldZoom) / oldZoom
    const distanceX = e.clientX - box.left
    const distanceY = e.clientY - box.top

    const dx = distanceX * ratio * -1
    const dy = distanceY * ratio * -1
    this.graph.dragMove(dx, dy)
  }
}

class KeyDriver {
  private graph: ActionGraphElement

  constructor(graph: ActionGraphElement) {
    this.graph = graph
    this.graph.addEventListener('keydown', (event: KeyboardEvent) => this.down(event))
  }

  private moveBothX(x: number) {
    this.graph.dragMove(x, 0)
  }

  private moveBothY(y: number) {
    this.graph.dragMove(0, y)
  }

  private down(e: KeyboardEvent) {
    // TODO: Refactor to use data-hotkey
    /* eslint eslint-comments/no-use: off */
    /* eslint-disable @github-ui/ui-commands/no-manual-shortcut-logic */
    let handled = true

    switch (e.key) {
      case 'ArrowLeft':
        this.moveBothX(100)
        break
      case 'ArrowUp':
        this.moveBothY(30)
        break
      case 'ArrowRight':
        this.moveBothX(-100)
        break
      case 'ArrowDown':
        this.moveBothY(-30)
        break
      default:
        handled = false
    }

    if (handled) {
      e.preventDefault()
    }
    /* eslint-enable @github-ui/ui-commands/no-manual-shortcut-logic */
  }
}

type Direction = '' | 'up' | 'down'

type Rect = {
  x: number
  y: number
  w: number
  h: number

  adjust: number
}

type LineTarget = {
  id: string

  rect: Rect

  isGroup: boolean
}

type Line = {
  path: SVGPathElement

  from: LineTarget

  to: LineTarget

  direction: Direction
}

type DependencyNode = {
  incoming: string[]

  outgoing: string[]
}

const HIGHLIGHT_CLASS = 'active'
const HIGHLIGHT_CLASS_IN = 'active--in'
const HIGHLIGHT_CLASS_OUT = 'active--out'

@controller
class ActionGraphElement extends HTMLElement {
  @target canvas: HTMLDivElement
  @target visualGraph: HTMLDivElement
  @target zoomFit: HTMLButtonElement
  @target zoomIn: HTMLButtonElement
  @target zoomOut: HTMLButtonElement
  @target stages: HTMLElement
  @target connectorLineHighlights: SVGElement
  @targets connectorLines: SVGElement[]
  @targets paths: SVGPathElement[]
  @targets pathHighlights: SVGPathElement[]
  @targets jobs: HTMLDivElement[]
  @targets matrices: HTMLDivElement[]

  readonly ZOOM_INCREMENT = 0.25
  readonly MAX_ZOOM = 1
  private currentZoom = 1
  private minZoom = 1

  private allHighlightedGroupIds: string[] = []
  private dependencies: {[id: string]: DependencyNode} = {}

  async connectedCallback() {
    // add mouse and keyboard drivers
    new MouseDriver(this)
    new KeyDriver(this)

    document.addEventListener('run-sidebar-job-enter', e => this.jobEnter(e))
    document.addEventListener('run-sidebar-job-leave', () => this.jobLeave())

    await loaded
    this.drawLines()
    this.fitToWindow()

    this.visualGraph.style.opacity = '1'
  }

  public cancelEvent(e: MouseEvent) {
    // 1. Firefox doesn't honor the `draggable="false"` attribute, the only way to prevent HTML5 drag/drop
    // API placeholders from showing up is to cancel the `dragstart` event
    // 2. Safari always displays a text cursor when dragging unless we cancel the selectionstart event
    e.preventDefault()
    e.stopPropagation()
  }

  // When a job on the graph is updated, call drawLines in case the job changed in size
  public async updateJob({target: job}: {target: HTMLElement}) {
    await updateContent(job)
    this.drawLines()
  }

  // Click on one of the expand matrix buttons
  public async expandMatrix(e: Event) {
    const el = e.target as HTMLElement
    const url = el.closest('button')?.getAttribute('data-update-url')
    const matrix = el.closest('.js-graph-matrix') as HTMLElement

    if (!matrix || !url) {
      return
    }
    matrix.setAttribute('data-url', url)

    await updateContent(matrix)

    this.fitToWindow(false)
    this.drawLines()
  }

  public getCurrentZoom(): number {
    return this.currentZoom
  }

  public dragMove(dx: number, dy: number) {
    const x = Math.round((parseFloat(this.visualGraph.getAttribute('data-x')!) || 0) + dx)
    const y = Math.round((parseFloat(this.visualGraph.getAttribute('data-y')!) || 0) + dy)

    this.visualGraph.style.transform = `translate(${x}px, ${y}px)`
    this.visualGraph.setAttribute('data-x', `${x}`)
    this.visualGraph.setAttribute('data-y', `${y}`)
    this.setFitToWindowBtnEnabled(true)
  }

  public setFitToWindowBtnEnabled(enabled: boolean) {
    this.zoomFit.disabled = !enabled
  }

  public setNormalizedZoom(zoom: number) {
    let newZoom = zoom

    if (newZoom < this.minZoom) {
      newZoom = this.minZoom
    } else if (zoom > this.MAX_ZOOM) {
      newZoom = this.MAX_ZOOM
    }

    this.setZoom(newZoom)
  }

  // Click on Zoom IN button
  private in() {
    this.zoomOut.disabled = false
    this.setFitToWindowBtnEnabled(true)
    this.setNormalizedZoom(this.currentZoom + this.ZOOM_INCREMENT)
  }

  // Click on Zoom OUT button
  private out() {
    this.zoomIn.disabled = false
    this.setFitToWindowBtnEnabled(true)
    this.setNormalizedZoom(this.currentZoom - this.ZOOM_INCREMENT)
  }

  // Click on fit button
  private fit() {
    this.setFitToWindowBtnEnabled(false)
    this.fitToWindow()
  }

  private setZoomInBtnDisabled(disabled: boolean) {
    this.zoomIn.disabled = disabled
  }

  private setZoomOutBtnDisabled(disabled: boolean) {
    this.zoomOut.disabled = disabled
  }

  private setZoom(zoom: number) {
    this.currentZoom = zoom
    this.setZoomInBtnDisabled(this.MAX_ZOOM === this.currentZoom)
    this.setZoomOutBtnDisabled(this.minZoom === this.currentZoom)
    this.stages.style.transform = `scale(${this.currentZoom})`
  }

  private fitToWindow(applyTransformation = true) {
    // Make sure the canvas fits inside the window with some horizontal/vertical buffer
    const canvasWidth = this.clientWidth - 48
    const canvasHeight = this.clientHeight - 75
    const workflowWidth = this.stages.clientWidth
    const workflowHeight = this.stages.clientHeight

    const xZoom = canvasWidth / workflowWidth
    const yZoom = canvasHeight / workflowHeight
    const zoom = Math.min(Math.min(xZoom, yZoom), this.MAX_ZOOM)
    this.minZoom = Math.min(zoom, this.MAX_ZOOM)

    // Move the graph to its origin (0,0) and zoom
    if (applyTransformation) {
      this.setZoom(zoom)
      this.visualGraph.style.transform = 'translate(0, 0)'
      this.visualGraph.setAttribute('data-x', '0')
      this.visualGraph.setAttribute('data-y', '0')
    }
  }

  private mouseEnter(e: MouseEvent) {
    this.setHighlightedGroup((e.target as HTMLElement).id)
  }

  private mouseLeave() {
    this.clearHighlightedGroups()
  }

  private jobEnter(e: Event) {
    const jobId = (e as CustomEvent).detail?.jobId
    if (!jobId) {
      return
    }

    let groupChild = this.matrices.find(m => m.getAttribute('data-job-id') === jobId)
    if (!groupChild) {
      groupChild = this.jobs.find(j => j.getAttribute('data-job-id') === jobId)
    }
    const groupId = groupChild?.getAttribute('data-group-id')
    if (groupId) {
      this.setDownstreamHighlightGroups(groupId)
    }
  }

  private jobLeave() {
    this.clearHighlightedGroups()
  }

  // Highlight all groups downstream of the given node
  private setDownstreamHighlightGroups(id: string) {
    this.allHighlightedGroupIds = [id]

    this.visualGraph.classList.add(HIGHLIGHT_CLASS)

    // Highlight the given element and its outgoing dependencies
    this.toggleClasses(id, [HIGHLIGHT_CLASS, HIGHLIGHT_CLASS_OUT], true)

    // Highlight connector lines between the set of highlighted groups
    this.connectorLineHighlights.classList.add(HIGHLIGHT_CLASS)

    if (!this.dependencies[id]) {
      // No connections for this node, no need to highlight any connected nodes or lines
      return
    }

    const downstreamGroups = new Set(this.getDownstreamIds(id, 0))

    // Highlight all downstream groups
    for (const g of downstreamGroups) {
      this.toggleClasses(g, [HIGHLIGHT_CLASS, HIGHLIGHT_CLASS_IN, HIGHLIGHT_CLASS_OUT], true)
    }

    this.allHighlightedGroupIds = this.allHighlightedGroupIds.concat([...downstreamGroups])

    for (const p of this.pathHighlights) {
      const from = p.getAttribute('data-from')!
      const to = p.getAttribute('data-to')!

      // Highlight all connectors between the node and a downstream group OR between two downstream groups
      if ((from === id || downstreamGroups.has(from)) && downstreamGroups.has(to)) {
        p.classList.add(HIGHLIGHT_CLASS)
      }
    }
  }

  // Highlight groups immediately before or after the given node
  private setHighlightedGroup(id: string) {
    this.allHighlightedGroupIds = [id]

    this.visualGraph.classList.add(HIGHLIGHT_CLASS)

    // Highlight the given element and its dependencies
    this.toggleClasses(id, [HIGHLIGHT_CLASS, HIGHLIGHT_CLASS_IN, HIGHLIGHT_CLASS_OUT], true)

    // Highlight connector lines between the set of highlighted groups
    this.connectorLineHighlights.classList.add(HIGHLIGHT_CLASS)

    if (!this.dependencies[id]) {
      // No connections for this node, no need to highlight any connected nodes or lines
      return
    }

    for (const dep of this.dependencies[id]?.incoming ?? []) {
      this.toggleClasses(dep, [HIGHLIGHT_CLASS, HIGHLIGHT_CLASS_IN], true)
    }

    for (const dep of this.dependencies[id]?.outgoing ?? []) {
      this.toggleClasses(dep, [HIGHLIGHT_CLASS, HIGHLIGHT_CLASS_OUT], true)
    }

    const highlightGroups = new Set([id, ...this.dependencies[id]!.incoming, ...this.dependencies[id]!.outgoing])
    this.allHighlightedGroupIds = [...highlightGroups]

    for (const p of this.pathHighlights) {
      const from = p.getAttribute('data-from')!
      const to = p.getAttribute('data-to')!

      // We want the highlighted node to be either the input or the output of this connector and the other
      // end needs to be in the set of highlighted nodes.
      if ((from === id || to === id) && (highlightGroups.has(from) || highlightGroups.has(to))) {
        p.classList.add(HIGHLIGHT_CLASS)
      }
    }
  }

  private getDownstreamIds(id: string, depth: number) {
    if (depth >= MAX_RECURSION_DEPTH) {
      return []
    }
    let result: string[] = []

    for (const d of this.dependencies[id]?.incoming ?? []) {
      result.push(d)
      result = result.concat(this.getDownstreamIds(d, depth + 1))
    }

    return result
  }

  private toggleClasses(id: string, classes: string[], force: boolean) {
    const highlightedElement = document.getElementById(id)

    if (!highlightedElement) {
      return
    }

    for (const c of classes) {
      highlightedElement.classList.toggle(c, force)
    }
  }

  private clearHighlightedGroups() {
    this.visualGraph.classList.remove(HIGHLIGHT_CLASS)

    if (this.allHighlightedGroupIds.length === 0) {
      return
    }

    const highlightClasses = [HIGHLIGHT_CLASS, HIGHLIGHT_CLASS_IN, HIGHLIGHT_CLASS_OUT]

    for (const g of this.allHighlightedGroupIds) {
      const element = document.getElementById(g)
      if (element) {
        for (const c of highlightClasses) {
          element.classList.remove(c)
        }
      }
    }

    for (const p of this.pathHighlights) {
      p.classList.remove(HIGHLIGHT_CLASS)
    }

    this.allHighlightedGroupIds = []
  }

  private drawLines() {
    // Adjust size of lines svg
    for (const connectorLines of this.connectorLines) {
      connectorLines.setAttribute('width', `${this.stages.clientWidth}`)
      connectorLines.setAttribute('height', `${this.stages.clientHeight}`)
    }

    // Pre-compute the lines and calculate the overall curve radius
    let radius = DESIRED_CONNECTOR_RADIUS

    this.dependencies = {}

    const lines: Line[] = this.paths.reduce((linesAcc, p) => {
      const from = this.getLineTarget(p.getAttribute('data-from'))
      const to = this.getLineTarget(p.getAttribute('data-to'))
      if (!from || !to) {
        return linesAcc
      }

      const line: Line = {
        path: p,
        from,
        to,
        direction: this.getPathDirection(from, to),
      }

      if (line.direction !== '') {
        const verticalDistanceHalf = Math.abs(from.rect.y - to.rect.y) / 2
        radius = Math.min(radius, verticalDistanceHalf)
      }

      // Store dependency information (both directions)
      for (const [origin, dest, direction] of [
        [from.id, to.id, 'incoming'],
        [to.id, from.id, 'outgoing'],
      ] as Array<[string, string, keyof DependencyNode]>) {
        if (!this.dependencies[origin]) {
          this.dependencies[origin] = {
            incoming: [],
            outgoing: [],
            [direction]: [dest],
          }
        } else {
          this.dependencies[origin]![direction].push(dest)
        }
      }

      linesAcc.push(line)
      return linesAcc
    }, [] as Line[])

    // Update the actual lines with the calculated radius
    for (const line of lines) {
      this.renderLine(line, radius)
    }
  }

  private getLineTarget(id: string | null): LineTarget | undefined {
    if (!id) {
      return
    }
    const element = document.getElementById(id)
    if (!element) {
      return
    }
    const rect = this.getRect(element)

    return {
      id,
      rect,
      isGroup: !!element?.classList?.contains('WorkflowCard-group'),
    }
  }

  private renderLine(line: Line, radius: number) {
    const pathString = this.getPath(radius, line.from, line.to, line.direction)
    line.path.setAttribute('d', pathString)
  }

  private getPathDirection(from: LineTarget, to: LineTarget): Direction {
    const fromRect = from.rect
    const toRect = to.rect

    if (fromRect.y === toRect.y) {
      return ''
    }

    if (fromRect.x < toRect.x) {
      return fromRect.y > toRect.y ? 'up' : 'down'
    }

    return fromRect.y > toRect.y ? 'down' : 'up'
  }

  private getRect(el: HTMLElement): Rect {
    let yAdjust = 23
    if (el.classList && el.classList.contains('WorkflowCard-group')) {
      yAdjust = 39 // This matches `top` in the SCSS
    }

    // Horizontal alignment
    // There a different scenario here, we want to maintain a constant gap between stages/columns
    // but the column width can be different depending on whether there are only groups, or
    // only jobs, or both. So we determine the horizontal width marked with X in the following
    // diagram:
    // +------------+---+--------+
    // |X+--------+ |   +--------+
    // |X|  Job   | | G |  Job   |
    // |X+--------+ | A +--------+
    // |            | P |        |
    // +------------+   |        |
    // |   Group    |   |        |
    // +------------+---+--------+

    const offsetParent = el.parentElement!
    const offsetX = el.offsetLeft - offsetParent.offsetLeft

    return {
      x: el.offsetLeft || 0,
      y: yAdjust + (el.offsetTop || 0),
      h: el.offsetHeight || 0,
      w: el.offsetWidth || 0,
      adjust: offsetX,
    }
  }

  private getPath(radius: number, from: LineTarget, to: LineTarget, direction: Direction): string {
    const xAdjust = to.rect.adjust
    const fromRect = from.rect
    const toRect = to.rect

    if (fromRect.y === toRect.y) {
      // Straight line
      return `M ${fromRect.x + fromRect.w} ${fromRect.y} H ${toRect.x}`
    } else if (direction === 'down') {
      const startCurveX1 =
        toRect.x -
        DISTANCE_BETWEEN_CARDS / 2 - // half the distance between cards
        radius -
        xAdjust
      const startCurveY1 = fromRect.y
      const startCurveX2 = startCurveX1 + radius
      const startCurveY2 = toRect.y - radius

      return `
      M ${fromRect.x + fromRect.w} ${fromRect.y}
      H ${startCurveX1}
      C ${startCurveX1 + radius / 2} ${startCurveY1} ${startCurveX1 + radius} ${startCurveY1 + radius / 2} ${
        startCurveX1 + radius
      } ${startCurveY1 + radius}
      V ${startCurveY2}
      C ${startCurveX2} ${startCurveY2 + radius / 2} ${startCurveX2 + radius / 2} ${startCurveY2 + radius} ${
        startCurveX2 + radius
      } ${startCurveY2 + radius}
      H ${toRect.x}`
    } else if (direction === 'up') {
      const startCurveX1 =
        toRect.x -
        DISTANCE_BETWEEN_CARDS / 2 - // half the distance between cards
        radius -
        xAdjust
      const startCurveY1 = fromRect.y
      const startCurveX2 = startCurveX1 + radius
      const startCurveY2 = toRect.y + radius

      return `
      M ${fromRect.x + fromRect.w} ${fromRect.y}
      H ${startCurveX1}
      C ${startCurveX1 + radius / 2} ${startCurveY1} ${startCurveX1 + radius} ${startCurveY1 - radius / 2} ${
        startCurveX1 + radius
      } ${startCurveY1 - radius}
      V ${startCurveY2}
      C ${startCurveX2} ${startCurveY2 - radius / 2} ${startCurveX2 + radius / 2} ${startCurveY2 - radius} ${
        startCurveX2 + radius
      } ${startCurveY2 - radius}
      H ${toRect.x}`
    }

    return ''
  }
}
